/**
 * The member Profile panel a STAFF session sees (DNKADM-0361) — every field rendered
 * from the member read the header already loaded, and nothing fetched.
 *
 * **Why not `MemberProfileTab`.** That editor is registry-driven: it reads
 * `GET /v2/admin/member-profile-fields` for the field set and
 * `GET /v2/admin/members/{contactId}/profile-values` for each enriched value. Neither is
 * in the column dink-api OPENS to the floor role — §2 opens the `/v2/admin/members` list
 * and the `/{contactId}` profile GET, and everything not listed is `403` — and
 * `member-profile-fields` is named in the closed column outright. It is also fail-CLOSED
 * by design: a registry load failure renders error + Retry, so for a STAFF session it
 * would render exactly the broken panel this unit exists to prevent.
 *
 * So the Profile a STAFF session gets is the profile the OPEN read carries, laid out
 * read-only: name, email, phone, member since, membership, home location, coach, and the
 * contact id. No edits, no coach or home-location setter, no photo control — those are
 * the header's write affordances, and the header withholds them for the same session.
 * (Decision recorded in the delivery: render from the read, never through a `403`
 * sub-fetch.)
 *
 * `contactId` is the external contact-system id — DATA, never surfaced as a vendor
 * name (§4.0 / white-label).
 */
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { MemberDetail as MemberDetailData } from '@/api/members';
import { formatPhoneForDisplay } from '@/components/phone/phoneFormat';
import { useCalendarZone } from '@/location/useCalendarZone';
import { MONO, resolveTokens } from '@/theme';
import { coachAssignmentState, coachDisplayName } from './coachChange';
import { homeName } from './homeLocationChange';
import { PLACEHOLDER, accessEndingLabel, formatJoined, membersName } from './memberDetailShared';

export function StaffMemberProfilePanel({ data }: { data: MemberDetailData }) {
  const t = resolveTokens(useTheme());
  const calendarZone = useCalendarZone();
  const coachText =
    coachAssignmentState(data.coach) === 'unassigned' ? PLACEHOLDER : coachDisplayName(data.coach);
  const rows: { label: string; value: string; mono?: boolean }[] = [
    { label: 'Name', value: membersName(data) },
    { label: 'Email', value: data.email ?? PLACEHOLDER },
    {
      label: 'Phone',
      value: data.phone !== null ? formatPhoneForDisplay(data.phone) : PLACEHOLDER,
    },
    { label: 'Member since', value: formatJoined(data.joinedAt, calendarZone) },
    { label: 'Membership', value: data.membershipTier ?? PLACEHOLDER },
    {
      label: 'Status',
      // DNKAPI-0600: the effective status, or "Active until {last day}" when it is ending.
      value: accessEndingLabel(data.membershipStatus, data) ?? data.membershipStatus ?? PLACEHOLDER,
    },
    {
      label: 'Home location',
      // `homeName` degrades to '' on a nameless, id-less record — the placeholder
      // is the honest reading of that, not an empty row.
      value:
        (data.homeLocation !== null ? homeName(data.homeLocation) : '') || PLACEHOLDER,
    },
    { label: 'Coach', value: coachText },
    { label: 'Member ID', value: data.id ?? PLACEHOLDER, mono: true },
  ];

  return (
    <Paper elevation={1} sx={{ p: '22px 24px' }}>
      <Typography variant="h3" component="h2" sx={{ mb: 1.5 }}>
        Profile
      </Typography>
      <Stack component="dl" sx={{ gap: 0, m: 0 }}>
        {rows.map((row, i) => (
          <Stack
            key={row.label}
            direction={{ xs: 'column', sm: 'row' }}
            sx={{
              py: 1.25,
              gap: { xs: 0.25, sm: 2 },
              borderBottom: i === rows.length - 1 ? 'none' : `1px solid ${t.lineFaint}`,
            }}
          >
            <Box component="dt" sx={{ width: 150, flex: 'none', fontSize: 13, color: t.text2 }}>
              {row.label}
            </Box>
            <Box
              component="dd"
              sx={{ m: 0, fontSize: 14, fontFamily: row.mono === true ? MONO : undefined }}
            >
              {row.value}
            </Box>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}
